/**
 * What the front page actually draws, resolved into strings the view can use without deciding
 * anything.
 *
 * <h2>The clinic's row is the page</h2>
 *
 * Every clinic's front page is stored whole: the platform writes the starter copy into a new
 * clinic's row, in both languages, the moment the clinic is created, and a migration did the
 * same for the older ones. So there is nothing here to fall back to. What the row says is what
 * the page draws -- which is also what lets the console's editor open on the real words, since
 * the fields and the page read the same thing.
 *
 * It was not always so. The row used to hold only what a clinic had rewritten, and this module
 * filled every blank with copy shipped in the locale files. The editor could show only the
 * first half of that, so an admin opened it on a column of empty boxes.
 *
 * <h2>The two rules</h2>
 *
 * **A blank line is not on the page.** It resolves to `''`, and {@link LandingPageView} leaves
 * the element out rather than drawing an empty pill or heading. That is how an admin takes a
 * line off.
 *
 * **Language falls back to Romanian.** Romanian is what a clinic writes; English is optional,
 * and a blank English line shows the Romanian one to an English reader.
 *
 * The platform's own page is the exception to all of this: it is product copy addressed to a
 * clinic considering Carefull, nobody configures it, and it stays in the locale files.
 */

import { TranslateFn } from '../validationRules';
import ResourcesIds from '../../resources/ResourcesIds';
import { localised } from './localisedText';
import { ITenantHome, ITenantHomeCard } from './tenantHome';

/** One card, resolved. */
export interface HomeCardContent {
    /** Stable for the list. A stored card's id, or the platform card's position. */
    key: string;
    /** A name from the curated set, or null for the default tile. */
    icon: string | null;
    title: string;
    body: string;
}

/** The whole page, resolved: every field is a string the view can draw without deciding anything. */
export interface HomeContent {
    heroEyebrow: string;
    heroHeadline1: string;
    heroHeadline2: string;
    heroSubtitle: string;
    featuresEyebrow: string;
    featuresTitle: string;
    featuresSubtitle: string;
    ctaTitle: string;
    ctaSubtitle: string;
    /** Absolute URL of the clinic's hero photograph, or null for the plain band. */
    heroImageUrl: string | null;
    cards: HomeCardContent[];
}

/**
 * The platform's own front page, on carefull.ro and on any address nobody has registered.
 *
 * Not configurable by anyone and deliberately so: it addresses a clinic considering Carefull,
 * where every clinic-facing page addresses a patient. It is resolved through the same shape as
 * a clinic's so that the view never has to know which of the two it is drawing.
 */
export const platformHomeContent = (translate: TranslateFn): HomeContent => ({
    heroEyebrow: translate(ResourcesIds.HERO_EYEBROW),
    heroHeadline1: translate(ResourcesIds.HERO_HEADLINE_LINE1),
    heroHeadline2: translate(ResourcesIds.HERO_HEADLINE_LINE2),
    heroSubtitle: translate(ResourcesIds.HERO_SUBTITLE),
    featuresEyebrow: translate(ResourcesIds.FEATURES_EYEBROW),
    featuresTitle: translate(ResourcesIds.FEATURES_HEADING),
    featuresSubtitle: translate(ResourcesIds.FEATURES_SUBHEADING),
    ctaTitle: translate(ResourcesIds.CTA_TITLE),
    ctaSubtitle: translate(ResourcesIds.CTA_SUBTITLE),
    heroImageUrl: null,
    cards: [
        card('platform-0', 'VERIFIED', translate(ResourcesIds.FEATURE_CERTIFIED_EXPERTS_TITLE),
            translate(ResourcesIds.FEATURE_CERTIFIED_EXPERTS_DESC)),
        card('platform-1', 'SCHEDULE', translate(ResourcesIds.FEATURE_FLEXIBLE_SCHEDULING_TITLE),
            translate(ResourcesIds.FEATURE_FLEXIBLE_SCHEDULING_DESC)),
        card('platform-2', 'LOCAL_HOSPITAL', translate(ResourcesIds.FEATURE_PERSONALIZED_CARE_TITLE),
            translate(ResourcesIds.FEATURE_PERSONALIZED_CARE_DESC)),
        card('platform-3', 'SECURITY', translate(ResourcesIds.FEATURE_SECURE_PRIVATE_TITLE),
            translate(ResourcesIds.FEATURE_SECURE_PRIVATE_DESC)),
    ],
});

/** A clinic page with nothing on it, which {@link clinicHomeContent} fills in part. */
const EMPTY_CLINIC_PAGE: HomeContent = {
    heroEyebrow: '',
    heroHeadline1: '',
    heroHeadline2: '',
    heroSubtitle: '',
    featuresEyebrow: '',
    featuresTitle: '',
    featuresSubtitle: '',
    ctaTitle: '',
    ctaSubtitle: '',
    heroImageUrl: null,
    cards: [],
};

/**
 * A clinic's front page: exactly what its row says.
 *
 * `stored` is undefined while the row is being fetched and when fetching it failed. The page
 * shows a skeleton for the first, so what this resolves to matters for the second: the clinic's
 * name as the headline and nothing else. That is the clinic's own data rather than words of
 * ours, and it is enough for a visitor to know whose door this is and still reach sign-in.
 *
 * Text is used verbatim -- no placeholder is filled in. The clinic's name is already written
 * into the starter lines that mention it.
 */
export const clinicHomeContent = (
    stored: ITenantHome | undefined,
    language: string,
    clinicName: string,
    heroImageUrl: string | null
): HomeContent => {
    const text = (ro: string | null | undefined, en: string | null | undefined): string =>
        localised(ro ?? '', en, language).trim();

    if (stored === undefined) {
        return { ...EMPTY_CLINIC_PAGE, heroHeadline1: clinicName, heroImageUrl };
    }

    return {
        heroEyebrow: text(stored.heroEyebrowRo, stored.heroEyebrowEn),
        heroHeadline1: text(stored.heroHeadline1Ro, stored.heroHeadline1En),
        heroHeadline2: text(stored.heroHeadline2Ro, stored.heroHeadline2En),
        heroSubtitle: text(stored.heroSubtitleRo, stored.heroSubtitleEn),
        featuresEyebrow: text(stored.featuresEyebrowRo, stored.featuresEyebrowEn),
        featuresTitle: text(stored.featuresTitleRo, stored.featuresTitleEn),
        featuresSubtitle: text(stored.featuresSubtitleRo, stored.featuresSubtitleEn),
        ctaTitle: text(stored.ctaTitleRo, stored.ctaTitleEn),
        ctaSubtitle: text(stored.ctaSubtitleRo, stored.ctaSubtitleEn),
        heroImageUrl,
        cards: (stored.cards ?? []).map((storedCard: ITenantHomeCard) => ({
            key: String(storedCard.id),
            icon: storedCard.icon ?? null,
            title: localised(storedCard.titleRo, storedCard.titleEn, language),
            body: localised(storedCard.bodyRo, storedCard.bodyEn, language),
        })),
    };
};

const card = (key: string, icon: string, title: string, body: string): HomeCardContent =>
    ({ key, icon, title, body });
